import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { logout } from '../store/authSlice';
import {
  ReactFlow,
  Background,
  Controls,
  Node,
  Edge,
  NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import Sidebar from '../components/common/Sidebar';
import BuilderEmptyState from '../components/interview-builder/BuilderEmptyState';
import JobRoleSearch from '../components/interview-builder/JobRoleSearch';
import JobRoleModalStack, { MAX_ROLE_MODAL_STACK, RoleModalEntry } from '../components/interview-builder/JobRoleModalStack';
import { ConfigPanel } from '../components/interview-builder';
import InterviewDetailModal from '../components/interview-builder/InterviewDetailModal';
import { ResumeEditorModal } from '../components/resume-editor';
import { parseMarkdownToLexical } from '../components/resume-editor/utils/generateResumeContent';
import { JDNode, RoundNode, AddRoundNode, ExtractSkillsNode } from '../components/interview-builder/nodes';
import type { JDNodeData, RoundNodeData, AddRoundNodeData, ExtractSkillsNodeData } from '../components/interview-builder/nodes';
import {
  InterviewNodeData,
  InterviewType,
  IndividualConfig,
  CodingConfig,
  GDConfig,
  INTERVIEW_TYPES,
  getInterviewTypeMeta,
  getDefaultConfig,
} from '../types/interviewBuilder';
import { useJDProcessing } from '../hooks/useJDProcessing';
import {
  customiseResume,
  deleteCustomInterview,
  fetchUserCustomInterviews,
  fetchCustomisedResume,
  scheduleCustomInterview,
  updateCustomInterview,
  pollJDStatus,
  CustomInterviewListItem,
  JDStatusResponse,
} from '../api/interviewBuilderApi';
import type { InterviewStatus } from '../hooks/useJDProcessing';
import { UpcomingCustomInterview } from '../api/roundsApi';
import { fetchJobInfoRole } from '../api/jobInfoApi';
import { getOrgDepartmentLabels, onboardingApi } from '../api/onboardingApi';
import { vibeService } from '../services/VibeService';
import { applySuggestedPipeline, mapRoundsForBackendSchedule } from '../utils/suggestedPipelineUtils';
import { buildUpcomingFromPipeline } from '../utils/interviewDetailUtils';
import './BuildInterview.css';

const nodeTypes: NodeTypes = {
  jdNode: JDNode,
  roundNode: RoundNode,
  addRoundNode: AddRoundNode,
  extractSkillsNode: ExtractSkillsNode,
};

// Helper to render interview type icons as SVGs (matching interview widget type-options)
const InterviewTypeIcon: React.FC<{ icon: string; color: string; size?: number }> = ({ icon, color, size = 20 }) => {
  switch (icon) {
    case 'triangle': // Mock Interview - Robot/screen icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 8V4H8" />
          <rect x="4" y="8" width="16" height="12" rx="2" />
          <circle cx="9" cy="13" r="1" fill={color} />
          <circle cx="15" cy="13" r="1" fill={color} />
          <path d="M9 17h6" />
        </svg>
      );
    case 'square': // Coding - Code brackets icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
          <line x1="14" y1="4" x2="10" y2="20" />
        </svg>
      );
    case 'diamond': // Group Discussion - People icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    default:
      return null;
  }
};

interface RoundSlot {
  id: string;
  name: string;
  interview: InterviewNodeData | null;
  scheduledDate?: string; // ISO date string
}

const MAX_ROUNDS = 7;

// Saved pipeline interface
interface SavedPipeline {
  id: string;
  name: string;
  rounds: RoundSlot[];
  jobDescription: string;
  createdAt: string;
  startsAt: string; // First scheduled date/time
  deadline?: string; // Interview completion deadline (ISO)
  status?: 'draft' | 'saved' | 'published';
  interviewId?: string; // Backend custom_interview ID
  interviewStatus?: InterviewStatus;
  jdProcessingStatus?: string | null;
}

const normalizeStoredPipelineStatus = (
  status?: string,
): SavedPipeline['status'] | undefined => {
  if (status === 'scheduled') return 'published';
  if (status === 'draft' || status === 'saved' || status === 'published') return status;
  return undefined;
};

const mapInterviewStatusToPipelineStatus = (
  interviewStatus?: string | null,
  jdProcessingStatus?: string | null,
): SavedPipeline['status'] => {
  if (interviewStatus === 'published') return 'published';
  if (jdProcessingStatus === 'processed') return 'saved';
  return 'draft';
};

const applyPipelineStatusFromJd = (
  pipeline: SavedPipeline,
  jdData: Pick<JDStatusResponse, 'interviewStatus' | 'jdProcessingStatus'>,
): SavedPipeline => ({
  ...pipeline,
  interviewStatus: jdData.interviewStatus ?? pipeline.interviewStatus,
  jdProcessingStatus: jdData.jdProcessingStatus ?? pipeline.jdProcessingStatus,
  status: mapInterviewStatusToPipelineStatus(
    jdData.interviewStatus ?? pipeline.interviewStatus,
    jdData.jdProcessingStatus ?? pipeline.jdProcessingStatus,
  ),
});

const PIPELINE_STATUS_PRIORITY: Record<NonNullable<SavedPipeline['status']>, number> = {
  draft: 1,
  saved: 2,
  published: 3,
};

const resolvePipelineStatus = (
  ...statuses: (SavedPipeline['status'] | undefined)[]
): SavedPipeline['status'] => {
  let best: SavedPipeline['status'] = 'draft';
  for (const status of statuses) {
    if (!status) continue;
    if (PIPELINE_STATUS_PRIORITY[status] > PIPELINE_STATUS_PRIORITY[best]) {
      best = status;
    }
  }
  return best;
};

const getScheduleErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === 'object' && first !== null && 'msg' in first) {
        return String((first as { msg: string }).msg);
      }
    }
  }
  return 'Failed to schedule interview. Please try again.';
};

// Helper function to format date for datetime-local input (uses local timezone)
const formatDateTimeLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const SCHEDULE_DEADLINE_DAYS = 7;

const getDefaultInterviewDeadline = (): string => {
  const deadline = new Date();
  deadline.setDate(deadline.getDate() + SCHEDULE_DEADLINE_DAYS);
  return deadline.toISOString();
};

/** Fill missing or past round times so scheduling can proceed without manual date entry. */
const ensureRoundsReadyForSchedule = (slots: RoundSlot[]): RoundSlot[] => {
  const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
  const validTimes = slots
    .filter((slot) => slot.interview && slot.scheduledDate)
    .map((slot) => new Date(slot.scheduledDate!).getTime())
    .filter((time) => time >= oneHourFromNow.getTime());

  let nextSlotTime = new Date(oneHourFromNow);
  if (validTimes.length > 0) {
    nextSlotTime = new Date(Math.max(...validTimes) + 60 * 60 * 1000);
  }

  return slots.map((slot) => {
    if (!slot.interview) {
      return { ...slot, scheduledDate: undefined };
    }
    const hasValidDate =
      slot.scheduledDate && new Date(slot.scheduledDate) >= oneHourFromNow;
    if (hasValidDate) {
      return slot;
    }
    const scheduledDate = formatDateTimeLocal(nextSlotTime);
    nextSlotTime = new Date(nextSlotTime.getTime() + 60 * 60 * 1000);
    return { ...slot, scheduledDate };
  });
};

const formatSavedInterviewName = (name: string) =>
  name.length > 10 ? `${name.slice(0, 10)}...` : name;

const SAVED_INTERVIEW_NAME_LIMIT = 10;

const DEFAULT_ROUND_SLOTS: RoundSlot[] = [
  { id: 'slot-1', name: 'Round 1', interview: null },
  { id: 'slot-2', name: 'Round 2', interview: null },
  { id: 'slot-3', name: 'Round 3', interview: null },
];

/** Load rounds stored on the pipeline only — never fall back to global canvas localStorage. */
const resolveRoundSlots = (pipeline: SavedPipeline): RoundSlot[] => {
  if (pipeline.rounds.length > 0) {
    return pipeline.rounds.map((slot, index) => ({
      ...slot,
      name: slot.name || `Round ${index + 1}`,
    }));
  }
  return DEFAULT_ROUND_SLOTS.map((slot) => ({ ...slot }));
};

const countFilledRounds = (slots: RoundSlot[]) =>
  slots.filter((slot) => slot.interview).length;

const BuildInterview: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [displayName, setDisplayName] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string | undefined>();
  const [departmentName, setDepartmentName] = useState<string | undefined>();

  // Fetch user profile for first_name/last_name from student table
  useEffect(() => {
    if (!user?.id) return;
    const loadProfile = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        const { first_name, last_name } = profileResponse.data;
        if (first_name && last_name) {
          setDisplayName(`${first_name} ${last_name}`.trim());
        } else if (first_name || last_name) {
          setDisplayName((first_name || last_name || '').trim());
        } else {
          setDisplayName(user?.email?.split('@')[0] || 'User');
        }
        const orgDept = getOrgDepartmentLabels(profileResponse.data);
        setOrganizationName(orgDept.organizationName);
        setDepartmentName(orgDept.departmentName);
      } catch {
        setDisplayName(user?.email?.split('@')[0] || 'User');
        setOrganizationName(undefined);
        setDepartmentName(undefined);
      }
    };
    loadProfile();
  }, [user?.id, user?.email]);

  // Current pipeline name - load from localStorage
  const [pipelineName, setPipelineName] = useState(() => {
    const saved = localStorage.getItem('build-interview-pipeline-name');
    return saved || 'New Interview';
  });
  const [isEditingName, setIsEditingName] = useState(false);
  const [pipelineNameError, setPipelineNameError] = useState('');

  // Saved pipelines - load from localStorage
  const [savedPipelines, setSavedPipelines] = useState<SavedPipeline[]>(() => {
    const saved = localStorage.getItem('build-interview-saved-pipelines');
    if (saved) {
      try {
        return (JSON.parse(saved) as SavedPipeline[]).map((pipeline) => ({
          ...pipeline,
          status: normalizeStoredPipelineStatus(pipeline.status) ?? pipeline.status,
        }));
      } catch {
        return [];
      }
    }
    return [];
  });

  // Loading and empty state for initial fetch
  const [isLoadingInterviews, setIsLoadingInterviews] = useState(true);
  const [hasInterviews, setHasInterviews] = useState(false);
  const [canvasEngaged, setCanvasEngaged] = useState(false);

  // Initialize with 3 empty slots - load from localStorage
  const [rounds, setRounds] = useState<RoundSlot[]>(() => {
    const saved = localStorage.getItem('build-interview-rounds');
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as RoundSlot[];
        // Ensure correct numbering on load
        return parsed.map((slot, index) => ({
          ...slot,
          name: `Round ${index + 1}`,
        }));
      } catch {
        return [
          { id: 'slot-1', name: 'Round 1', interview: null },
          { id: 'slot-2', name: 'Round 2', interview: null },
          { id: 'slot-3', name: 'Round 3', interview: null },
        ];
      }
    }
    return [
      { id: 'slot-1', name: 'Round 1', interview: null },
      { id: 'slot-2', name: 'Round 2', interview: null },
      { id: 'slot-3', name: 'Round 3', interview: null },
    ];
  });
  
  // Selected slot for configuration
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  
  // Drag state
  const [dragOverSlotId, setDragOverSlotId] = useState<string | null>(null);
  const [draggingSlotId, setDraggingSlotId] = useState<string | null>(null);
  
  // Sidebar collapse state - collapsed by default on Build Interview page
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const canvasRef = useRef<HTMLDivElement>(null);
  
  // Config panel visibility - open by default to show JD config
  const [isPanelOpen, setIsPanelOpen] = useState(true);

  // JD node state - selected by default so user sees JD config panel first
  const [isJDSelected, setIsJDSelected] = useState(true);
  const [pastedText, setPastedText] = useState('');
  
  // Word count for pasted text
  const wordCount = useMemo(() => {
    const trimmed = pastedText.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [pastedText]);
  
  const isWordCountInvalid = wordCount > 0 && (wordCount < 10 || wordCount > 650);
  const isWordCountValid = wordCount >= 10 && wordCount <= 650;
  
  // Extracted info modal + resume editor (post-schedule customise only)
  const [extractedInfoInterview, setExtractedInfoInterview] = useState<UpcomingCustomInterview | null>(null);
  const [extractedInfoResume, setExtractedInfoResume] = useState<{
    exists: boolean;
    resumeMarkdown?: string;
  } | null>(null);
  const [isCheckingExtractedInfoResume, setIsCheckingExtractedInfoResume] = useState(false);
  const [isCustomisingResume, setIsCustomisingResume] = useState(false);
  const [isResumeEditorOpen, setIsResumeEditorOpen] = useState(false);
  const [resumeEditorContent, setResumeEditorContent] = useState<string>('');
  
  // JD processing hook (submit, poll, retry)
  const jdProcessing = useJDProcessing();
  const isExtractingSkills = jdProcessing.isProcessing;
  const isExtractionComplete = jdProcessing.isComplete;
  const backendInterviewsRef = useRef<Map<string, CustomInterviewListItem>>(new Map());

  // Thinking message cycling for extraction loading state
  const [thinkingMessage, setThinkingMessage] = useState<string>('');
  
  // ReactFlow instance ref for programmatic control
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reactFlowInstanceRef = useRef<any>(null);
  const hasRestoredPipelineRef = useRef(false);
  const hasFetchedInterviewsRef = useRef(false);
  
  // Render key to force ReactFlow re-render when needed
  const [renderKey, setRenderKey] = useState(0);

  const fitCanvasView = useCallback(() => {
    setTimeout(() => {
      reactFlowInstanceRef.current?.fitView({ padding: 0.2, duration: 300 });
    }, 120);
  }, []);
  
  // Handle closing resume editor - force re-render and realign
  const handleCloseResumeEditor = useCallback(() => {
    setIsResumeEditorOpen(false);
    setRenderKey(prev => prev + 1);
    setTimeout(() => {
      if (reactFlowInstanceRef.current) {
        reactFlowInstanceRef.current.fitView({ padding: 0.2, duration: 300 });
      }
    }, 100);
  }, []);

  const handleCloseExtractedInfoModal = useCallback(() => {
    setExtractedInfoInterview(null);
    setExtractedInfoResume(null);
  }, []);

  const handleCustomiseFromExtractedInfoModal = useCallback(async () => {
    if (!extractedInfoInterview) return;

    setIsCustomisingResume(true);

    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_ATTEMPTS = 60;

    try {
      await customiseResume(extractedInfoInterview.id, 'medium');

      let attempts = 0;
      const pollForResume = async (): Promise<void> => {
        attempts++;
        if (attempts > MAX_POLL_ATTEMPTS) {
          throw new Error('Resume customisation timed out. Please try again.');
        }

        const { data } = await fetchCustomisedResume(extractedInfoInterview.id);

        if (data.status === 'completed' && data.resumeMarkdown) {
          const lexicalContent = parseMarkdownToLexical(data.resumeMarkdown);
          setResumeEditorContent(JSON.stringify(lexicalContent));
          setExtractedInfoResume({ exists: true, resumeMarkdown: data.resumeMarkdown });
          handleCloseExtractedInfoModal();
          setIsResumeEditorOpen(true);
          return;
        }

        if (data.status === 'failed') {
          throw new Error('Resume customisation failed. Please try again.');
        }

        if (data.status === 'queued' || data.status === 'processing') {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          return pollForResume();
        }

        throw new Error('Unexpected status received');
      };

      await pollForResume();
    } catch (err) {
      console.error('[BuildInterview] Failed to customise resume:', err);
    } finally {
      setIsCustomisingResume(false);
    }
  }, [extractedInfoInterview, handleCloseExtractedInfoModal]);
  
  // Auto-realign when rounds change or canvas layout shifts
  useEffect(() => {
    fitCanvasView();
  }, [rounds.length, fitCanvasView]);

  useEffect(() => {
    fitCanvasView();
  }, [isPanelOpen, hasInterviews, fitCanvasView]);

  useEffect(() => {
    if (jdProcessing.isProcessing || jdProcessing.isComplete) {
      fitCanvasView();
    }
  }, [jdProcessing.isProcessing, jdProcessing.isComplete, fitCanvasView]);

  // Date validation warning
  const [dateWarning, setDateWarning] = useState<'conflict' | 'past' | null>(null);
  const [dateWarningClosing, setDateWarningClosing] = useState(false);
  
  // Save pipeline errors and success message
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  
  // Delete confirmation and animation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [savedNameTooltip, setSavedNameTooltip] = useState<{
    name: string;
    top: number;
    left: number;
  } | null>(null);
  const [roleModalStack, setRoleModalStack] = useState<RoleModalEntry[]>([]);
  const [isBuildingFromRole, setIsBuildingFromRole] = useState(false);
  const [searchClearSignal, setSearchClearSignal] = useState(0);
  
  // Currently active/editing pipeline - restore from localStorage on refresh
  const [activePipelineId, setActivePipelineId] = useState<string | null>(() => {
    return localStorage.getItem('build-interview-active-pipeline');
  });

  const activePipeline = activePipelineId
    ? savedPipelines.find(p => p.id === activePipelineId)
    : undefined;

  const handleOpenExtractedInfoModal = useCallback(async () => {
    if (!jdProcessing.interviewId || !jdProcessing.isComplete) return;

    const interview = buildUpcomingFromPipeline({
      interviewId: jdProcessing.interviewId,
      name: pipelineName,
      jobDescription: pastedText,
      deadline: activePipeline?.deadline ?? activePipeline?.startsAt,
      extractedData: jdProcessing.extractedData,
      jdProcessingStatus: jdProcessing.status,
    });

    setExtractedInfoInterview(interview);
    setExtractedInfoResume(null);
    setIsCheckingExtractedInfoResume(true);

    try {
      const { data } = await fetchCustomisedResume(jdProcessing.interviewId);
      if (data.status === 'completed' && data.resumeMarkdown) {
        setExtractedInfoResume({ exists: true, resumeMarkdown: data.resumeMarkdown });
      } else {
        setExtractedInfoResume({ exists: false });
      }
    } catch {
      setExtractedInfoResume({ exists: false });
    } finally {
      setIsCheckingExtractedInfoResume(false);
    }
  }, [
    jdProcessing.interviewId,
    jdProcessing.isComplete,
    jdProcessing.extractedData,
    jdProcessing.status,
    pipelineName,
    pastedText,
    activePipeline?.deadline,
    activePipeline?.startsAt,
  ]);

  const handleConfirmDelete = useCallback((pipeline: SavedPipeline) => {
    setDeleteConfirmId(null);
    setDeletingId(pipeline.id);

    if (pipeline.interviewId) {
      deleteCustomInterview(pipeline.interviewId).catch(err =>
        console.error('Failed to delete custom interview:', err)
      );
    }

    if (activePipelineId === pipeline.id) {
      setActivePipelineId(null);
      setCanvasEngaged(false);
      jdProcessing.reset();
    }

    setTimeout(() => {
      setSavedPipelines(prev => prev.filter(p => p.id !== pipeline.id));
      setDeletingId(null);
    }, 600);
  }, [activePipelineId, jdProcessing]);

  const defaultRoundSlots = useCallback(
    (): RoundSlot[] => DEFAULT_ROUND_SLOTS.map((slot) => ({ ...slot })),
    [],
  );

  const persistActivePipelineSnapshot = useCallback(
    (prev: SavedPipeline[]): SavedPipeline[] => {
      if (!activePipelineId) return prev;
      return prev.map((p) =>
        p.id === activePipelineId
          ? {
              ...p,
              rounds,
              jobDescription: pastedText,
              name: pipelineName,
            }
          : p
      );
    },
    [activePipelineId, rounds, pastedText, pipelineName],
  );

  const submitSmartBuildWithThinking = useCallback((params: {
    roleId: string;
    responsibilityLevel?: number;
    deadline?: string;
  }) => {
    const thinkingMessages = [
      'understanding role',
      'extracting skills',
      'analyzing requirements',
      'building knowledge graph',
      'scoring extraction',
    ];
    let messageIndex = 0;
    setThinkingMessage(thinkingMessages[0]);
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % thinkingMessages.length;
      setThinkingMessage(thinkingMessages[messageIndex]);
    }, 2000);

    vibeService.trigger('thinking');
    jdProcessing.smartBuild(params).finally(() => {
      clearInterval(messageInterval);
    });
  }, [jdProcessing]);

  const handleCloseRoleModal = useCallback(() => {
    setRoleModalStack(prev => prev.slice(0, -1));
    setIsBuildingFromRole(false);
  }, []);

  const handleCloseAllRoleModals = useCallback(() => {
    setRoleModalStack([]);
    setIsBuildingFromRole(false);
  }, []);

  const pushRoleModal = useCallback(async (roleId: string) => {
    const entryId = `role-modal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setRoleModalStack(prev => {
      const next = [
        ...prev,
        { id: entryId, roleId, role: null, isLoading: true, error: null },
      ];
      return next.slice(-MAX_ROLE_MODAL_STACK);
    });

    try {
      const { data } = await fetchJobInfoRole(roleId);
      setRoleModalStack(prev =>
        prev.map(entry =>
          entry.id === entryId
            ? { ...entry, role: data, isLoading: false, error: null }
            : entry
        )
      );
    } catch {
      setRoleModalStack(prev =>
        prev.map(entry =>
          entry.id === entryId
            ? { ...entry, isLoading: false, error: 'Failed to load role details' }
            : entry
        )
      );
    }
  }, []);

  const handleRoleSelectFromSearch = useCallback((roleId: string) => {
    setRoleModalStack([]);
    void pushRoleModal(roleId);
  }, [pushRoleModal]);

  const handleRelatedRoleSelect = useCallback((roleId: string) => {
    void pushRoleModal(roleId);
  }, [pushRoleModal]);

  const handleBuildFromRole = useCallback((selectedLevel?: number) => {
    const topModal = roleModalStack[roleModalStack.length - 1];
    const roleDetail = topModal?.role;
    if (!roleDetail) return;

    setIsBuildingFromRole(true);

    const levelInfo =
      selectedLevel != null
        ? roleDetail.responsibility_levels.find((level) => level.level === selectedLevel)
        : undefined;
    const pipelineLabel = levelInfo
      ? `${roleDetail.role_name} — ${levelInfo.level_name}`
      : roleDetail.role_name;

    const newId = `pipeline-${Date.now()}`;
    const draftPipeline: SavedPipeline = {
      id: newId,
      name: pipelineLabel,
      rounds: defaultRoundSlots(),
      jobDescription: '',
      createdAt: new Date().toISOString(),
      startsAt: '',
      status: 'draft',
    };

    setSavedPipelines((prev) => [...persistActivePipelineSnapshot(prev), draftPipeline]);
    setActivePipelineId(newId);
    setPipelineName(pipelineLabel);
    setPastedText('');
    setRounds(defaultRoundSlots());
    setSelectedSlotId(null);
    setIsPanelOpen(false);
    setCanvasEngaged(true);
    hasRestoredPipelineRef.current = true;
    setThinkingMessage('');
    setHasInterviews(true);
    setPipelineNameError('');
    setSearchClearSignal(prev => prev + 1);
    handleCloseAllRoleModals();
    submitSmartBuildWithThinking({
      roleId: roleDetail.role_id,
      ...(selectedLevel != null && { responsibilityLevel: selectedLevel }),
    });
    setIsBuildingFromRole(false);
    fitCanvasView();
    setRenderKey(prev => prev + 1);
  }, [
    roleModalStack,
    defaultRoundSlots,
    persistActivePipelineSnapshot,
    handleCloseAllRoleModals,
    submitSmartBuildWithThinking,
    fitCanvasView,
  ]);
  
  // Close date warning with animation
  const closeDateWarning = useCallback(() => {
    setDateWarningClosing(true);
    setTimeout(() => {
      setDateWarning(null);
      setDateWarningClosing(false);
    }, 300); // Match CSS animation duration
  }, []);
  
  // Date sync mode - sync all round dates with 1 hour intervals
  const [isDateSyncEnabled, setIsDateSyncEnabled] = useState(() => {
    const saved = localStorage.getItem('build-interview-date-sync');
    return saved === 'true';
  });
  
  // Persist rounds to localStorage
  useEffect(() => {
    localStorage.setItem('build-interview-rounds', JSON.stringify(rounds));
  }, [rounds]);
  
  // Persist pipeline name to localStorage and sync to active saved pipeline
  useEffect(() => {
    localStorage.setItem('build-interview-pipeline-name', pipelineName);
    if (activePipelineId) {
      setSavedPipelines(prev =>
        prev.map(p => p.id === activePipelineId ? { ...p, name: pipelineName } : p)
      );
    }
  }, [pipelineName, activePipelineId]);

  // Persist active pipeline ID to localStorage
  useEffect(() => {
    if (activePipelineId) {
      localStorage.setItem('build-interview-active-pipeline', activePipelineId);
    } else {
      localStorage.removeItem('build-interview-active-pipeline');
    }
  }, [activePipelineId]);

  // Sync thinkingMessage and JD text with processing status
  useEffect(() => {
    if (jdProcessing.status === 'processed') {
      setThinkingMessage('Extraction complete');
    } else if (jdProcessing.status === 'violation') {
      setThinkingMessage('');
    } else if (jdProcessing.status === 'failed') {
      setThinkingMessage('');
    }
    // Restore JD text from saved interview if not already set (non-smart-build path)
    if (jdProcessing.jobDescription && !pastedText && !jdProcessing.suggestedPipeline) {
      setPastedText(jdProcessing.jobDescription);
    }
  }, [jdProcessing.status, jdProcessing.jobDescription, jdProcessing.suggestedPipeline]);

  const appliedSuggestedPipelineKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (jdProcessing.status === 'queued') {
      appliedSuggestedPipelineKeyRef.current = null;
    }
  }, [jdProcessing.status]);

  // Apply suggested pipeline from smart build as soon as the status response includes it
  useEffect(() => {
    const { suggestedPipeline, interviewId, jobDescription } = jdProcessing;
    if (!suggestedPipeline || !interviewId) return;

    const applyKey = `${interviewId}:${suggestedPipeline.name}:${suggestedPipeline.rounds.length}`;
    if (appliedSuggestedPipelineKeyRef.current === applyKey) return;
    appliedSuggestedPipelineKeyRef.current = applyKey;

    const applied = applySuggestedPipeline(suggestedPipeline, jobDescription);

    setPipelineName(applied.name);
    if (applied.jobDescription) {
      setPastedText(applied.jobDescription);
    }
    setRounds((current) =>
      current.some((slot) => slot.interview) ? current : applied.rounds
    );
    setSelectedSlotId(null);
    setIsPanelOpen(false);
    setPipelineNameError('');

    setSavedPipelines(prev => {
      if (!activePipelineId) return prev;
      return prev.map(p =>
        p.id === activePipelineId
          ? {
              ...p,
              name: applied.name,
              jobDescription: applied.jobDescription || p.jobDescription,
              startsAt: applied.startsAt || p.startsAt,
              status: mapInterviewStatusToPipelineStatus(
                jdProcessing.interviewStatus,
                'processed',
              ),
              interviewId,
            }
          : p
      );
    });
    fitCanvasView();
    setRenderKey(prev => prev + 1);
  }, [
    jdProcessing.suggestedPipeline,
    jdProcessing.interviewId,
    jdProcessing.jobDescription,
    jdProcessing.interviewStatus,
    activePipelineId,
    fitCanvasView,
  ]);

  // Update the active saved pipeline when extraction completes (JD paste path)
  useEffect(() => {
    if (
      jdProcessing.status === 'processed' &&
      jdProcessing.interviewId &&
      !jdProcessing.suggestedPipeline
    ) {
      const iid = jdProcessing.interviewId;
      setSavedPipelines(prev => {
        // First try: update the currently active pipeline (e.g. a draft created by +)
        if (activePipelineId) {
          const active = prev.find(p => p.id === activePipelineId);
          if (active) {
            return prev.map(p =>
              p.id === activePipelineId
                ? {
                    ...p,
                    name: pipelineName,
                    status: mapInterviewStatusToPipelineStatus(
                      jdProcessing.interviewStatus,
                      'processed',
                    ),
                    interviewStatus: jdProcessing.interviewStatus ?? p.interviewStatus,
                    jdProcessingStatus: 'processed',
                    interviewId: iid,
                    jobDescription: pastedText,
                  }
                : p
            );
          }
        }
        // Second try: find by interviewId (e.g. restored from backend)
        const byIid = prev.find(p => p.interviewId === iid);
        if (byIid) {
          return prev.map(p =>
            p.interviewId === iid
              ? {
                  ...p,
                  name: pipelineName,
                  status: mapInterviewStatusToPipelineStatus(
                    jdProcessing.interviewStatus,
                    'processed',
                  ),
                  interviewStatus: jdProcessing.interviewStatus ?? p.interviewStatus,
                  jdProcessingStatus: 'processed',
                }
              : p
          );
        }
        // No fallback - pipelines should only be created via the + button
        return prev;
      });
    }
  }, [jdProcessing.status, jdProcessing.interviewId, jdProcessing.interviewStatus, pipelineName, pastedText, activePipelineId]);

  // Keep saved pipeline status in sync with JD status API for the active interview
  useEffect(() => {
    const { interviewId, interviewStatus, status: jdProcessingStatus } = jdProcessing;
    if (!interviewId || !interviewStatus) return;

    setSavedPipelines(prev =>
      prev.map(p =>
        p.interviewId === interviewId
          ? {
              ...p,
              interviewStatus,
              jdProcessingStatus,
              status: mapInterviewStatusToPipelineStatus(interviewStatus, jdProcessingStatus),
            }
          : p
      )
    );
  }, [jdProcessing.interviewId, jdProcessing.interviewStatus, jdProcessing.status]);
  
  // Sort rounds by scheduled date and renumber them
  const sortRoundsByDate = useCallback(() => {
    setRounds((prev) => {
      // Separate filled and empty slots
      const filledSlots = prev.filter(slot => slot.interview && slot.scheduledDate);
      const filledWithoutDate = prev.filter(slot => slot.interview && !slot.scheduledDate);
      const emptySlots = prev.filter(slot => !slot.interview);
      
      // Sort filled slots by scheduled date
      filledSlots.sort((a, b) => {
        const dateA = new Date(a.scheduledDate!).getTime();
        const dateB = new Date(b.scheduledDate!).getTime();
        return dateA - dateB;
      });
      
      // Combine: sorted filled slots, then filled without dates, then empty slots
      const sorted = [...filledSlots, ...filledWithoutDate, ...emptySlots];
      
      // Check if order actually changed
      const orderChanged = sorted.some((slot, index) => slot.id !== prev[index].id);
      if (!orderChanged) return prev;
      
      // Renumber all rounds
      return sorted.map((slot, index) => ({
        ...slot,
        name: `Round ${index + 1}`,
      }));
    });
  }, []);

  // Persist date sync setting to localStorage
  useEffect(() => {
    localStorage.setItem('build-interview-date-sync', String(isDateSyncEnabled));
  }, [isDateSyncEnabled]);
  
  // Persist saved pipelines to localStorage
  useEffect(() => {
    localStorage.setItem('build-interview-saved-pipelines', JSON.stringify(savedPipelines));
  }, [savedPipelines]);

  // Keep active saved pipeline in sync with canvas rounds
  useEffect(() => {
    if (!activePipelineId) return;
    setSavedPipelines(prev =>
      prev.map(p => (p.id === activePipelineId ? { ...p, rounds } : p))
    );
  }, [rounds, activePipelineId]);

  // Load backend interviews on mount, merge with local drafts, and auto-restore active pipeline
  useEffect(() => {
    if (hasFetchedInterviewsRef.current) return;
    hasFetchedInterviewsRef.current = true;

    let cancelled = false;
    setIsLoadingInterviews(true);
    vibeService.trigger('loading');
    fetchUserCustomInterviews()
      .then(({ data }) => {
        if (cancelled) return;
        const interviews = data.interviews || [];

        const map = new Map<string, CustomInterviewListItem>();
        interviews.forEach(i => map.set(i.id, i));
        backendInterviewsRef.current = map;

        const backendPipelines: SavedPipeline[] = interviews.map(i => ({
          id: `pipeline-${i.id}`,
          name: i.name,
          rounds: [],
          jobDescription: i.jobDescription,
          createdAt: i.createdAt,
          startsAt: '',
          status: mapInterviewStatusToPipelineStatus(i.status, i.jdProcessingStatus),
          interviewStatus: (i.status as InterviewStatus) || 'draft',
          jdProcessingStatus: i.jdProcessingStatus,
          interviewId: i.id,
        }));

        let localSaved: SavedPipeline[] = [];
        try {
          const raw = localStorage.getItem('build-interview-saved-pipelines');
          if (raw) localSaved = JSON.parse(raw) as SavedPipeline[];
        } catch { /* ignore */ }

        const localByInterviewId = new Map(
          localSaved
            .filter(p => p.interviewId)
            .map(p => [p.interviewId!, p]),
        );
        const localDrafts = localSaved.filter(p => !p.interviewId);

        const mergedBackend = backendPipelines.map(p => {
          const local = p.interviewId ? localByInterviewId.get(p.interviewId) : undefined;
          if (!local) return p;
          return {
            ...p,
            id: local.id || p.id,
            name: local.name || p.name,
            rounds: local.rounds?.length ? local.rounds : p.rounds,
            jobDescription: local.jobDescription || p.jobDescription,
            startsAt: local.startsAt || p.startsAt,
            status: resolvePipelineStatus(local.status, p.status),
          };
        });

        const mergedFromStorage = [...mergedBackend, ...localDrafts];

        setSavedPipelines(prev => {
          const mergedIds = new Set(mergedFromStorage.map(p => p.id));
          const prevById = new Map(prev.map(p => [p.id, p]));
          const mergedWithMemory = mergedFromStorage.map(p => {
            const inMemory = prevById.get(p.id);
            if (inMemory?.rounds?.some(slot => slot.interview)) {
              return {
                ...p,
                rounds: inMemory.rounds,
                name: inMemory.name || p.name,
                jobDescription: inMemory.jobDescription || p.jobDescription,
                startsAt: inMemory.startsAt || p.startsAt,
                status: resolvePipelineStatus(inMemory.status, p.status),
              };
            }
            return p;
          });
          const orphanDrafts = prev.filter(p => !mergedIds.has(p.id));
          return [...mergedWithMemory, ...orphanDrafts];
        });

        setHasInterviews(prev => prev || mergedFromStorage.length > 0);

        const interviewIds = Array.from(
          new Set(
            mergedFromStorage
              .map(p => p.interviewId)
              .filter((id): id is string => Boolean(id)),
          ),
        );

        if (interviewIds.length > 0) {
          Promise.all(
            interviewIds.map(async (id) => {
              try {
                const { data } = await pollJDStatus(id);
                return { id, data };
              } catch {
                return null;
              }
            }),
          ).then((results) => {
            if (cancelled) return;
            const statusByInterviewId = new Map(
              results
                .filter((result): result is { id: string; data: JDStatusResponse } => result !== null)
                .map(({ id, data }) => [id, data]),
            );
            if (statusByInterviewId.size === 0) return;

            setSavedPipelines(prev =>
              prev.map(p =>
                p.interviewId && statusByInterviewId.has(p.interviewId)
                  ? applyPipelineStatusFromJd(p, statusByInterviewId.get(p.interviewId)!)
                  : p
              )
            );
          });
        }

        if (hasRestoredPipelineRef.current) return;

        const storedActiveId = localStorage.getItem('build-interview-active-pipeline');
        if (!storedActiveId) return;

        let activePl = mergedFromStorage.find(p => p.id === storedActiveId);
        if (!activePl) {
          const previousLocal = localSaved.find(p => p.id === storedActiveId);
          if (previousLocal?.interviewId) {
            activePl = mergedFromStorage.find(p => p.interviewId === previousLocal.interviewId);
          }
        }
        if (!activePl) return;

        hasRestoredPipelineRef.current = true;

        setActivePipelineId(currentActiveId => currentActiveId ?? activePl!.id);
        setPipelineName(prev => prev === 'New Interview' ? activePl!.name : prev);
        setPastedText(prev => prev || activePl!.jobDescription);
        setRounds(current =>
          current.some(slot => slot.interview) ? current : resolveRoundSlots(activePl!)
        );

        if (activePl.interviewId) {
          const backendData = map.get(activePl.interviewId);
          if (backendData) {
            jdProcessing.loadFromInterview(backendData);
          }
        }
      })
      .catch(err => console.error('[BuildInterview] Failed to load interviews:', err))
      .finally(() => {
        if (!cancelled) {
          setIsLoadingInterviews(false);
          vibeService.dismiss();
        }
      });
    return () => { cancelled = true; };
  }, [jdProcessing.loadFromInterview, fitCanvasView]);
  
  // Sync all round dates with 1 hour intervals when enabled
  const syncRoundDates = useCallback((baseSlotId: string, baseDate: string) => {
    const baseIndex = rounds.findIndex(r => r.id === baseSlotId);
    if (baseIndex === -1 || !baseDate) return;
    
    const baseTime = new Date(baseDate);
    
    // Count only filled slots for calculating offsets
    let filledSlotOffset = 0;
    const baseFilledIndex = rounds.slice(0, baseIndex + 1).filter(r => r.interview).length - 1;
    
    setRounds((prev) => {
      let currentFilledIndex = 0;
      return prev.map((slot) => {
        // Only set dates for filled slots
        if (!slot.interview) {
          return { ...slot, scheduledDate: undefined };
        }
        
        // Calculate the time offset based on filled slot position
        const hourOffset = currentFilledIndex - baseFilledIndex;
        const newTime = new Date(baseTime.getTime() + hourOffset * 60 * 60 * 1000);
        
        // Format as datetime-local value (local timezone)
        const formattedDate = formatDateTimeLocal(newTime);
        
        currentFilledIndex++;
        return { ...slot, scheduledDate: formattedDate };
      });
    });
  }, [rounds]);
  
  // Handle date sync toggle
  const handleDateSyncToggle = useCallback(() => {
    const newSyncState = !isDateSyncEnabled;
    setIsDateSyncEnabled(newSyncState);
    
    // When enabling sync, use the currently selected round's date as base
    // If no selected round or it has no date, fall back to first round with a date
    if (newSyncState) {
      const selectedRound = selectedSlotId ? rounds.find(r => r.id === selectedSlotId) : null;
      
      if (selectedRound?.scheduledDate) {
        // Use the currently selected round's date as base
        syncRoundDates(selectedRound.id, selectedRound.scheduledDate);
      } else {
        // Fall back to first round with a date
        const firstRoundWithDate = rounds.find(r => r.scheduledDate);
        if (firstRoundWithDate?.scheduledDate) {
          syncRoundDates(firstRoundWithDate.id, firstRoundWithDate.scheduledDate);
        }
      }
    }
  }, [isDateSyncEnabled, rounds, selectedSlotId, syncRoundDates]);

  // Get selected slot data
  const selectedSlot = useMemo(() => {
    return rounds.find((slot) => slot.id === selectedSlotId);
  }, [rounds, selectedSlotId]);

  // Add a new round slot (max 7)
  const addRound = useCallback(() => {
    setRounds((prev) => {
      if (prev.length >= MAX_ROUNDS) return prev;
      const newId = `slot-${Date.now()}`;
      const newNumber = prev.length + 1;
      
      // New slots are empty, so no date is set (dates are only for filled slots)
      return [...prev, { id: newId, name: `Round ${newNumber}`, interview: null }];
    });
  }, []);

  // Remove a round slot and renumber remaining rounds
  const removeRound = useCallback((slotId: string) => {
    setRounds((prev) => {
      const filtered = prev.filter((slot) => slot.id !== slotId);
      // Renumber all rounds to maintain chronological order
      return filtered.map((slot, index) => ({
        ...slot,
        name: `Round ${index + 1}`,
      }));
    });
    if (selectedSlotId === slotId) {
      setIsPanelOpen(false);
      setSelectedSlotId(null);
    }
  }, [selectedSlotId]);

  // Rename a round
  const renameRound = useCallback((slotId: string, newName: string) => {
    setRounds((prev) =>
      prev.map((slot) =>
        slot.id === slotId ? { ...slot, name: newName } : slot
      )
    );
  }, []);

  // Check if a date/time is already used by another round
  const isDateTimeConflict = useCallback((date: string, excludeSlotId: string) => {
    return rounds.some(slot => 
      slot.id !== excludeSlotId && slot.scheduledDate === date
    );
  }, [rounds]);

  // Update scheduled date for a round
  const updateScheduledDate = useCallback((slotId: string, date: string) => {
    // Check if date is at least 1 hour in the future
    if (date) {
      const selectedDate = new Date(date);
      const now = new Date();
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
      if (selectedDate < oneHourFromNow) {
        // Show warning and don't update - date must be at least 1 hour in the future
        setDateWarning('past');
        setDateWarningClosing(false);
        setTimeout(() => closeDateWarning(), 3000);
        return;
      }
    }
    
    // Check for conflicts (same date AND time)
    if (date && isDateTimeConflict(date, slotId)) {
      // Show warning and don't update
      setDateWarning('conflict');
      setDateWarningClosing(false);
      setTimeout(() => closeDateWarning(), 3000);
      return;
    }
    
    // Clear any existing warning immediately (no animation needed for success)
    setDateWarning(null);
    setDateWarningClosing(false);
    
    if (isDateSyncEnabled) {
      // When sync is enabled, update all rounds with 1 hour intervals
      syncRoundDates(slotId, date);
    } else {
      // Normal update - just update this round, then sort
      setRounds((prev) =>
        prev.map((slot) =>
          slot.id === slotId ? { ...slot, scheduledDate: date } : slot
        )
      );
      // Sort rounds by date after update (use timeout to ensure state is updated first)
      setTimeout(() => sortRoundsByDate(), 100);
    }
  }, [isDateSyncEnabled, syncRoundDates, isDateTimeConflict, sortRoundsByDate]);

  // Handle drag start from dock - create custom drag image
  const onDragStart = useCallback((event: React.DragEvent, nodeType: InterviewType) => {
    event.dataTransfer.setData('application/interview-type', nodeType);
    event.dataTransfer.effectAllowed = 'move';

    // Create a custom drag image (square box with icon)
    const meta = getInterviewTypeMeta(nodeType);
    const dragImage = document.createElement('div');
    dragImage.className = 'build-interview__drag-ghost';
    dragImage.style.cssText = `
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #252525;
      border: 1px solid ${meta.color};
      border-radius: 10px;
      position: absolute;
      top: -1000px;
      left: -1000px;
      pointer-events: none;
    `;
    
    // Clone the icon SVG
    const iconElement = event.currentTarget.querySelector('svg');
    if (iconElement) {
      const clonedIcon = iconElement.cloneNode(true) as SVGElement;
      dragImage.appendChild(clonedIcon);
    }
    
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 24, 24);
    
    // Clean up the drag image after a short delay
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  }, []);

  // Swap two slots (for drag-and-drop reordering)
  const swapSlots = useCallback((sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    
    setRounds((prev) => {
      const sourceIndex = prev.findIndex(s => s.id === sourceId);
      const targetIndex = prev.findIndex(s => s.id === targetId);
      
      if (sourceIndex === -1 || targetIndex === -1) return prev;
      
      // Create a new array with swapped positions
      const newRounds = [...prev];
      const temp = newRounds[sourceIndex];
      newRounds[sourceIndex] = newRounds[targetIndex];
      newRounds[targetIndex] = temp;
      
      // Renumber all rounds
      return newRounds.map((slot, index) => ({
        ...slot,
        name: `Round ${index + 1}`,
      }));
    });
  }, []);

  // Handle slot drag start (for reordering)
  const onSlotDragStart = useCallback((event: React.DragEvent, slotId: string, accentColor: string) => {
    event.dataTransfer.setData('application/slot-id', slotId);
    event.dataTransfer.effectAllowed = 'move';
    setDraggingSlotId(slotId);
    
    // Create custom drag image
    const dragImage = document.createElement('div');
    dragImage.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(30, 30, 30, 0.95);
      border: 2px solid ${accentColor};
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(8px);
    `;
    
    // Clone the icon SVG from the slot
    const iconElement = event.currentTarget.querySelector('svg');
    if (iconElement) {
      const clonedIcon = iconElement.cloneNode(true) as SVGElement;
      clonedIcon.style.width = '24px';
      clonedIcon.style.height = '24px';
      dragImage.appendChild(clonedIcon);
    }
    
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 24, 24);
    
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  }, []);

  // Handle slot drag end
  const onSlotDragEnd = useCallback(() => {
    setDraggingSlotId(null);
    setDragOverSlotId(null);
  }, []);

  // Handle drag over slot
  const onDragOver = useCallback((event: React.DragEvent, slotId: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverSlotId(slotId);
  }, []);

  // Handle drag leave
  const onDragLeave = useCallback(() => {
    setDragOverSlotId(null);
  }, []);

  // Handle drop on slot
  const onDrop = useCallback((event: React.DragEvent, slotId: string) => {
    event.preventDefault();
    setDragOverSlotId(null);
    setDraggingSlotId(null);

    // Check if this is a slot reorder (dragging from another slot)
    const sourceSlotId = event.dataTransfer.getData('application/slot-id');
    if (sourceSlotId) {
      swapSlots(sourceSlotId, slotId);
      return;
    }

    // Otherwise, it's a new interview type from the dock
    const type = event.dataTransfer.getData('application/interview-type') as InterviewType;
    if (!type) return;

    const meta = getInterviewTypeMeta(type);
    const newInterview: InterviewNodeData = {
      label: meta.title,
      interviewType: type,
      config: getDefaultConfig(type),
      isConfigured: false,
    };

    // Calculate scheduled date - always default to 1 hour from now if no existing dates
    let scheduledDate: string;
    const filledSlotsWithDates = rounds.filter(r => r.interview && r.scheduledDate);
    
    if (filledSlotsWithDates.length > 0 && isDateSyncEnabled) {
      // If sync is enabled and there are existing dates, use 1 hour after last
      const lastFilledSlot = filledSlotsWithDates[filledSlotsWithDates.length - 1];
      const lastDate = new Date(lastFilledSlot.scheduledDate!);
      const newDate = new Date(lastDate.getTime() + 60 * 60 * 1000); // 1 hour after last
      scheduledDate = formatDateTimeLocal(newDate);
    } else {
      // Default: 1 hour from now
      const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
      scheduledDate = formatDateTimeLocal(oneHourFromNow);
    }

    setRounds((prev) =>
      prev.map((slot) =>
        slot.id === slotId ? { ...slot, interview: newInterview, scheduledDate } : slot
      )
    );
    setSelectedSlotId(slotId);
    setIsJDSelected(false);
    setIsPanelOpen(true); // Open config panel after drop
  }, [isDateSyncEnabled, rounds, swapSlots]);

  // Clear a slot (also clears the date since empty slots shouldn't have dates)
  const clearSlot = useCallback((slotId: string) => {
    setRounds((prev) =>
      prev.map((slot) =>
        slot.id === slotId ? { ...slot, interview: null, scheduledDate: undefined } : slot
      )
    );
    if (selectedSlotId === slotId) {
      setIsPanelOpen(false);
      setSelectedSlotId(null);
    }
  }, [selectedSlotId]);

  // Update configuration
  const onUpdateConfig = useCallback(
    (config: IndividualConfig | CodingConfig | GDConfig) => {
      if (!selectedSlotId) return;

      setRounds((prev) =>
        prev.map((slot) => {
          if (slot.id === selectedSlotId && slot.interview) {
            return {
              ...slot,
              interview: { ...slot.interview, config },
            };
          }
          return slot;
        })
      );
    },
    [selectedSlotId]
  );

  // Mark as configured
  const onMarkConfigured = useCallback(() => {
    if (!selectedSlotId) return;

    setRounds((prev) =>
      prev.map((slot) => {
        if (slot.id === selectedSlotId && slot.interview) {
          return {
            ...slot,
            interview: { ...slot.interview, isConfigured: true },
          };
        }
        return slot;
      })
    );
  }, [selectedSlotId]);

  // Validate and save pipeline
  const handleSavePipeline = useCallback(async () => {
    const errors: string[] = [];
    
    // 1. Check job description word count
    if (!isWordCountValid) {
      if (wordCount === 0) {
        errors.push('Job description is required (10-650 words)');
      } else if (wordCount < 10) {
        errors.push(`Job description too short (${wordCount}/10 words minimum)`);
      } else if (wordCount > 650) {
        errors.push(`Job description too long (${wordCount}/650 words maximum)`);
      }
    }
    
    // 2. Check for filled rounds (will be auto-configured on save)
    const filledRounds = rounds.filter(r => r.interview !== null);
    if (filledRounds.length === 0) {
      errors.push('At least one round with an interview type is required');
    }

    const interviewId = jdProcessing.interviewId;
    if (!interviewId) {
      errors.push('Interview must be processed before scheduling');
    }

    if (errors.length > 0) {
      setSaveErrors(errors);
      vibeService.trigger('error');
      return;
    }

    // Auto-assign round dates and 7-day interview deadline before validation
    const scheduleRounds = ensureRoundsReadyForSchedule(rounds);
    const deadline = getDefaultInterviewDeadline();
    if (JSON.stringify(scheduleRounds) !== JSON.stringify(rounds)) {
      setRounds(scheduleRounds);
    }

    const roundsForSchedule = scheduleRounds;
    const filledForSchedule = roundsForSchedule.filter((r) => r.interview !== null);

    // 3. Check for duplicate dates among filled rounds
    const dateMap = new Map<string, string[]>();
    filledForSchedule.forEach((round) => {
      if (round.scheduledDate) {
        const dateKey = round.scheduledDate;
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, []);
        }
        dateMap.get(dateKey)!.push(round.name);
      }
    });
    
    dateMap.forEach((roundNames, _) => {
      if (roundNames.length > 1) {
        errors.push(`${roundNames.join(' & ')}: Cannot have the same date and time`);
      }
    });
    
    setSaveErrors(errors);
    
    // If there are errors, trigger error vibe
    if (errors.length > 0) {
      vibeService.trigger('error');
      return;
    }

    // Auto-mark all filled rounds as configured
    const updatedRounds = roundsForSchedule.map(slot => {
      if (slot.interview) {
        return {
          ...slot,
          interview: { ...slot.interview, isConfigured: true }
        };
      }
      return slot;
    });
    setRounds(updatedRounds);
    
    // Find the earliest scheduled date
    const scheduledDates = filledForSchedule
      .filter(r => r.scheduledDate)
      .map(r => new Date(r.scheduledDate!).getTime())
      .sort((a, b) => a - b);
    const startsAt = scheduledDates.length > 0 
      ? new Date(scheduledDates[0]).toISOString() 
      : new Date().toISOString();
    
    const filledOnly = updatedRounds.filter(r => r.interview !== null);
    const backendRounds = mapRoundsForBackendSchedule(filledOnly);

    setIsScheduling(true);
    setSaveSuccess(false);

    try {
      await updateCustomInterview(interviewId!, {
        name: pipelineName,
        jobDescription: pastedText,
        rounds: backendRounds,
        deadline,
      });

      await scheduleCustomInterview(interviewId!);

      const { data: jdData } = await pollJDStatus(interviewId!);
      const publishedPipelineFields = {
        interviewStatus: jdData.interviewStatus ?? 'published',
        jdProcessingStatus: jdData.jdProcessingStatus,
        status: mapInterviewStatusToPipelineStatus(
          jdData.interviewStatus,
          jdData.jdProcessingStatus,
        ),
      };

      setSavedPipelines(prev => {
        if (activePipelineId) {
          const activeIdx = prev.findIndex(p => p.id === activePipelineId);
          if (activeIdx >= 0) {
            return prev.map((p, i) =>
              i === activeIdx
                ? {
                    ...p,
                    name: pipelineName,
                    rounds: filledOnly,
                    jobDescription: pastedText,
                    startsAt,
                    deadline,
                    interviewId: interviewId || p.interviewId,
                    ...publishedPipelineFields,
                  }
                : p
            );
          }
        }

        if (interviewId) {
          const byInterviewIdx = prev.findIndex(p => p.interviewId === interviewId);
          if (byInterviewIdx >= 0) {
            return prev.map((p, i) =>
              i === byInterviewIdx
                ? {
                    ...p,
                    name: pipelineName,
                    rounds: filledOnly,
                    jobDescription: pastedText,
                    startsAt,
                    deadline,
                    ...publishedPipelineFields,
                  }
                : p
            );
          }
        }

        const newPipeline: SavedPipeline = {
          id: `pipeline-${Date.now()}`,
          name: pipelineName,
          rounds: filledOnly,
          jobDescription: pastedText,
          createdAt: new Date().toISOString(),
          startsAt,
          deadline,
          interviewId: interviewId || undefined,
          ...publishedPipelineFields,
        };
        return [...prev, newPipeline];
      });

      const cachedInterview = backendInterviewsRef.current.get(interviewId!);
      if (cachedInterview) {
        backendInterviewsRef.current.set(interviewId!, {
          ...cachedInterview,
          status: 'published',
          name: pipelineName,
          jobDescription: pastedText,
        });
      }
      
      // Show success message
      setSaveSuccess(true);
      setSaveErrors([]);
      vibeService.trigger('celebration');
      setTimeout(() => setSaveSuccess(false), 5000);
      
      // Reset to new pipeline
      setPipelineName('New Interview');
      setPastedText('');
      setRounds([
        { id: 'slot-1', name: 'Round 1', interview: null },
        { id: 'slot-2', name: 'Round 2', interview: null },
        { id: 'slot-3', name: 'Round 3', interview: null },
      ]);
      setSelectedSlotId(null);
      setIsPanelOpen(false);
      setActivePipelineId(null);
      setCanvasEngaged(false);
    } catch (err) {
      console.error('[BuildInterview] Failed to schedule interview:', err);
      setSaveErrors([getScheduleErrorMessage(err)]);
      vibeService.trigger('error');
    } finally {
      setIsScheduling(false);
    }
  }, [
    isWordCountValid,
    wordCount,
    rounds,
    pipelineName,
    pastedText,
    jdProcessing.interviewId,
    activePipelineId,
  ]);

  // Close config panel
  const onCloseConfig = useCallback(() => {
    setSelectedSlotId(null);
  }, []);

  // Load a saved pipeline into the canvas and sync hook state
  const handleLoadPipeline = useCallback((pipeline: SavedPipeline) => {
    setSavedPipelines((prev) => persistActivePipelineSnapshot(prev));
    setActivePipelineId(pipeline.id);
    setPipelineName(pipeline.name);
    setPastedText(pipeline.jobDescription);
    setRounds(resolveRoundSlots(pipeline));
    setSelectedSlotId(null);
    setIsPanelOpen(false);
    setCanvasEngaged(true);

    if (pipeline.interviewId) {
      const backendData = backendInterviewsRef.current.get(pipeline.interviewId);
      if (backendData) {
        jdProcessing.loadFromInterview(backendData);
      } else {
        jdProcessing.reset();
        setThinkingMessage('');
      }
    } else {
      jdProcessing.reset();
      setThinkingMessage('');
    }
  }, [
    jdProcessing.loadFromInterview,
    jdProcessing.reset,
    persistActivePipelineSnapshot,
  ]);

  // Get pipeline stats
  const stats = useMemo(() => {
    const filled = rounds.filter((s) => s.interview !== null).length;
    const configured = rounds.filter((s) => s.interview?.isConfigured).length;
    return { total: rounds.length, filled, configured };
  }, [rounds]);

  const isExtractionLocked = activePipeline?.status === 'saved' || activePipeline?.status === 'published';

  const handleProcessJD = useCallback(() => {
    if (!isWordCountValid || isExtractionLocked) return;
    if (jdProcessing.isProcessing || jdProcessing.isComplete) return;

    const thinkingMessages = [
      'understanding JD',
      'extracting skills',
      'analyzing requirements',
      'building knowledge graph',
      'scoring extraction',
    ];
    let messageIndex = 0;
    setThinkingMessage(thinkingMessages[0]);
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % thinkingMessages.length;
      setThinkingMessage(thinkingMessages[messageIndex]);
    }, 2000);

    vibeService.trigger('thinking');

    const run =
      (jdProcessing.status === 'failed' || jdProcessing.status === 'violation') &&
      jdProcessing.interviewId
        ? jdProcessing.retry(pastedText)
        : jdProcessing.submit(pastedText, pipelineName, activePipeline?.interviewId);

    run.finally(() => clearInterval(messageInterval));
  }, [
    isWordCountValid,
    isExtractionLocked,
    jdProcessing,
    pastedText,
    pipelineName,
    activePipeline?.interviewId,
  ]);

  const isBuilderCanvasActive = useMemo(() => {
    if (!activePipelineId) return false;
    if (canvasEngaged) return true;
    const hasJdContent = pastedText.trim().length > 0;
    const hasFilledRounds = rounds.some((s) => s.interview);
    const hasJdWork =
      jdProcessing.status !== 'idle' || Boolean(jdProcessing.interviewId);
    return hasJdContent || hasFilledRounds || hasJdWork;
  }, [
    activePipelineId,
    canvasEngaged,
    pastedText,
    rounds,
    jdProcessing.status,
    jdProcessing.interviewId,
  ]);

  const showCanvasEmptyState = hasInterviews && !isBuilderCanvasActive;

  useEffect(() => {
    if (!showCanvasEmptyState) return;
    setIsPanelOpen(false);
    setIsJDSelected(false);
    setSelectedSlotId(null);
  }, [showCanvasEmptyState]);

  // Generate ReactFlow nodes from rounds state
  const reactFlowNodes = useMemo(() => {
    const PILL_WIDTH = 220;
    const NODE_GAP = 100;
    const START_X = 50;
    const CENTER_Y = 320;
    const UPPER_BRANCH_Y = 120;
    const LOWER_BRANCH_Y = 440;
    const BRANCH_START_X = 300;
    
    const nodes: Node[] = [];
    
    // JD Node - trigger node (center-left)
    const jdData: JDNodeData = {
      wordCount,
      isValid: isWordCountValid,
      isInvalid: isWordCountInvalid,
      isSelected: isJDSelected,
      onClick: () => {
        setIsJDSelected(true);
        setSelectedSlotId(null);
        setIsPanelOpen(true);
      },
    };
    nodes.push({
      id: 'jd',
      type: 'jdNode',
      position: { x: START_X, y: CENTER_Y },
      data: jdData as unknown as Record<string, unknown>,
      selected: isJDSelected,
    });

    // Extracted Info node (upper branch) — directly from JD
    const extractSkillsData: ExtractSkillsNodeData = {
      isProcessing: isExtractingSkills,
      isComplete: isExtractionComplete,
      isMuted: false,
      isClickable: isExtractionComplete,
      thinkingMessage: thinkingMessage,
      status: jdProcessing.status,
      violations: jdProcessing.violations,
      extractedSkills: jdProcessing.extractedData?.skills,
      onClick: handleOpenExtractedInfoModal,
      onRetry: () => {
        const messages = ['retrying extraction', 'processing JD', 'analyzing requirements'];
        let idx = 0;
        setThinkingMessage(messages[0]);
        const iv = setInterval(() => {
          idx = (idx + 1) % messages.length;
          setThinkingMessage(messages[idx]);
        }, 2000);
        jdProcessing.retry(pastedText).finally(() => clearInterval(iv));
      },
    };
    nodes.push({
      id: 'extract-skills',
      type: 'extractSkillsNode',
      position: { x: BRANCH_START_X, y: UPPER_BRANCH_Y },
      data: extractSkillsData as unknown as Record<string, unknown>,
    });

    // Round nodes (lower branch)
    rounds.forEach((slot, index) => {
      const meta = slot.interview ? getInterviewTypeMeta(slot.interview.interviewType) : null;
      // Muted logic: first round is never muted, subsequent rounds are muted if previous round has no interview
      const isMuted = index > 0 && !rounds[index - 1].interview;
      const roundData: RoundNodeData = {
        id: slot.id,
        name: slot.name,
        interview: slot.interview,
        scheduledDate: slot.scheduledDate,
        isSelected: selectedSlotId === slot.id,
        isDragOver: dragOverSlotId === slot.id,
        isDragging: draggingSlotId === slot.id,
        isMuted: isMuted,
        canRemove: rounds.length > 3 && !slot.interview,
        onDragOver: (e: React.DragEvent<HTMLDivElement>) => onDragOver(e, slot.id),
        onDragLeave: onDragLeave,
        onDrop: (e: React.DragEvent<HTMLDivElement>) => onDrop(e, slot.id),
        onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
          if (slot.interview && meta) {
            onSlotDragStart(e, slot.id, meta.color);
          }
        },
        onDragEnd: onSlotDragEnd,
        onClick: () => {
          if (slot.interview) {
            setSelectedSlotId(slot.id);
            setIsJDSelected(false);
            setIsPanelOpen(true);
          }
        },
        onClearSlot: () => clearSlot(slot.id),
        onRemoveRound: () => removeRound(slot.id),
      };
      nodes.push({
        id: slot.id,
        type: 'roundNode',
        position: { x: BRANCH_START_X + (PILL_WIDTH + NODE_GAP) * index, y: LOWER_BRANCH_Y },
        data: roundData as unknown as Record<string, unknown>,
        selected: selectedSlotId === slot.id,
      });
    });
    
    // Add Round node - always last (lower branch)
    const addRoundData: AddRoundNodeData = {
      isDisabled: rounds.length >= MAX_ROUNDS,
      onClick: addRound,
    };
    nodes.push({
      id: 'add-round',
      type: 'addRoundNode',
      position: { x: BRANCH_START_X + (PILL_WIDTH + NODE_GAP) * rounds.length, y: LOWER_BRANCH_Y + 10 },
      data: addRoundData as unknown as Record<string, unknown>,
    });
    
    return nodes;
  }, [
    rounds, wordCount, isWordCountValid, isWordCountInvalid, isJDSelected,
    selectedSlotId, dragOverSlotId, draggingSlotId,
    onDragOver, onDragLeave, onDrop, onSlotDragStart, onSlotDragEnd,
    clearSlot, removeRound, addRound,
    isExtractingSkills, isExtractionComplete, thinkingMessage,
    handleOpenExtractedInfoModal, jdProcessing.status, jdProcessing.violations,
    jdProcessing.extractedData, jdProcessing.retry, pastedText,
  ]);

  // Generate ReactFlow edges connecting nodes
  const reactFlowEdges = useMemo((): Edge[] => {
    const edges: Edge[] = [];
    const edgeColor = '#0EA5E9'; // Ocean blue
    const mutedEdgeColor = '#374151';
    const strokeWidth = 1.5;
    
    // JD to first round (lower branch) - curved
    if (rounds.length > 0) {
      edges.push({
        id: 'e-jd-0',
        source: 'jd',
        sourceHandle: 'right',
        target: rounds[0].id,
        type: 'bezier',
        style: { stroke: edgeColor, strokeWidth },
        animated: false,
      });
    }
    
    // Connect rounds to each other - straight horizontal
    for (let i = 0; i < rounds.length - 1; i++) {
      // Edge is muted if target round is muted (previous round has no interview)
      const isTargetMuted = !rounds[i].interview;
      edges.push({
        id: `e-${i}-${i + 1}`,
        source: rounds[i].id,
        target: rounds[i + 1].id,
        type: 'straight',
        style: { stroke: isTargetMuted ? mutedEdgeColor : edgeColor, strokeWidth },
        animated: false,
      });
    }
    
    // Last round to add-round button - straight horizontal dashed
    if (rounds.length > 0) {
      const lastRoundHasInterview = !!rounds[rounds.length - 1].interview;
      edges.push({
        id: 'e-last-add',
        source: rounds[rounds.length - 1].id,
        target: 'add-round',
        type: 'straight',
        style: { stroke: lastRoundHasInterview ? edgeColor : mutedEdgeColor, strokeWidth, strokeDasharray: '4,3' },
        animated: false,
      });
    }
    
    // JD to Extracted Info (upper branch) - curved
    edges.push({
      id: 'e-jd-extract',
      source: 'jd',
      sourceHandle: 'right',
      target: 'extract-skills',
      targetHandle: 'left',
      type: 'bezier',
      style: { stroke: edgeColor, strokeWidth },
      animated: false,
    });
    
    return edges;
  }, [rounds]);

  // User data for sidebar - from profile (first_name/last_name) or auth
  const fullName = displayName || user?.email?.split('@')[0] || 'User';
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const pipelineToDelete = deleteConfirmId
    ? savedPipelines.find(p => p.id === deleteConfirmId)
    : undefined;

  // Handler for creating a new pipeline (used by empty state and + button)
  const handleCreateNewPipeline = useCallback(() => {
    const newId = `pipeline-${Date.now()}`;
    const draftPipeline: SavedPipeline = {
      id: newId,
      name: 'New Interview',
      rounds: defaultRoundSlots(),
      jobDescription: '',
      createdAt: new Date().toISOString(),
      startsAt: '',
      status: 'draft',
    };
    setSavedPipelines((prev) => [...persistActivePipelineSnapshot(prev), draftPipeline]);
    setActivePipelineId(newId);
    setPipelineName('New Interview');
    setPastedText('');
    setRounds(defaultRoundSlots());
    setSelectedSlotId(null);
    setIsPanelOpen(true);
    setIsJDSelected(true);
    setCanvasEngaged(true);
    jdProcessing.reset();
    setThinkingMessage('');
    setHasInterviews(true);
  }, [jdProcessing, defaultRoundSlots, persistActivePipelineSnapshot]);

  // Loading state component
  const LoadingState = () => (
    <div className="build-interview__loading">
      <div className="loading-spinner"></div>
      <p>Loading interviews...</p>
    </div>
  );

  return (
    <div className="build-interview">
      <Sidebar
        activeNav="interviews"
        onSelectNav={() => navigate('/candidate')}
        initials={initials}
        name={fullName}
        email={user?.email}
        organizationName={organizationName}
        departmentName={departmentName}
        onStartInterview={() => navigate('/interview')}
        onLogout={async () => {
          try {
            await dispatch(logout()).unwrap();
            navigate('/login');
          } catch {
            navigate('/login');
          }
        }}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      {/* Conditional rendering: Loading -> Header + Empty/Workspace */}
      {isLoadingInterviews ? (
        <LoadingState />
      ) : (
        <div className="build-interview__main">
          <div className="build-interview__header">
            <div className="build-interview__header-left">
              <button className="build-interview__back-btn" onClick={() => navigate('/candidate')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="build-interview__title">Build Interview</h1>
                <p className="build-interview__subtitle">Build and schedule custom interview rounds</p>
              </div>
            </div>
            <div className="build-interview__header-center">
              <JobRoleSearch
                onRoleSelect={handleRoleSelectFromSearch}
                clearSignal={searchClearSignal}
              />
            </div>
            <div className="build-interview__header-right" aria-hidden="true" />
          </div>

          {!hasInterviews ? (
            <BuilderEmptyState variant="full" onNewInterview={handleCreateNewPipeline} />
          ) : (
        <div className="build-interview__workspace">
          {/* Left: Saved Pipelines Panel */}
          <div className="build-interview__pipelines-panel">
            <div className="build-interview__pipelines-header">
              <h3>Saved Interviews</h3>
              <button
                className="build-interview__new-pipeline-btn"
                title="New interview"
                onClick={handleCreateNewPipeline}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>

            {/* Saved Pipelines List */}
            <div className="build-interview__saved-list">
              {savedPipelines.length > 0 ? (
                savedPipelines.map((pipeline) => (
                  <div
                    key={pipeline.id}
                    className={`build-interview__saved-item ${deletingId === pipeline.id ? 'build-interview__saved-item--deleting' : ''} ${activePipelineId === pipeline.id ? 'build-interview__saved-item--active' : ''}`}
                    onMouseEnter={(e) => {
                      if (pipeline.name.length <= SAVED_INTERVIEW_NAME_LIMIT) return;
                      const info = e.currentTarget.querySelector('.build-interview__saved-item-info');
                      const rect = (info ?? e.currentTarget).getBoundingClientRect();
                      setSavedNameTooltip({
                        name: pipeline.name,
                        top: rect.top + rect.height / 2,
                        left: rect.right + 8,
                      });
                    }}
                    onMouseLeave={() => setSavedNameTooltip(null)}
                  >
                    <div 
                      className="build-interview__saved-item-info"
                      onClick={() => handleLoadPipeline(pipeline)}
                    >
                      <div className="build-interview__saved-name-row">
                        <span className="build-interview__saved-name">
                          {formatSavedInterviewName(pipeline.name)}
                        </span>
                      </div>
                      <span className="build-interview__saved-meta">
                        <span className="build-interview__saved-rounds">
                          {countFilledRounds(pipeline.rounds)} rounds
                        </span>
                        {pipeline.startsAt && (
                          <span className="build-interview__saved-starts">
                            starts at: {new Date(pipeline.startsAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                      </span>
                      {pipeline.status && (
                        <span className={`build-interview__saved-status build-interview__saved-status--${pipeline.status}`}>
                          {pipeline.status}
                        </span>
                      )}
                    </div>
                    <div className="build-interview__saved-item-actions">
                      <button 
                        className="build-interview__saved-action"
                        title="Edit"
                        onClick={() => handleLoadPipeline(pipeline)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button 
                        className="build-interview__saved-action"
                        title="Duplicate"
                        onClick={() => {
                          // Duplicate pipeline
                          const duplicatedPipeline: SavedPipeline = {
                            ...pipeline,
                            id: `pipeline-${Date.now()}`,
                            name: `${pipeline.name} (Copy)`,
                            createdAt: new Date().toISOString()
                          };
                          setSavedPipelines(prev => [...prev, duplicatedPipeline]);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>
                      <button 
                        className="build-interview__saved-action build-interview__saved-action--delete"
                        title="Delete"
                        onClick={() => setDeleteConfirmId(pipeline.id)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="build-interview__saved-empty">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span>No saved sequences</span>
                </div>
              )}

              {(pipelineToDelete || saveErrors.length > 0 || saveSuccess) && (
              <div className="build-interview__saved-list-footer">
                {pipelineToDelete && (
                  <div className="build-interview__delete-confirm">
                    <div className="build-interview__delete-confirm-text">
                      <span className="build-interview__delete-confirm-title">
                        Delete &ldquo;{pipelineToDelete.name}&rdquo;?
                      </span>
                      <span className="build-interview__delete-confirm-warning">
                        This action cannot be undone.
                      </span>
                    </div>
                    <div className="build-interview__delete-confirm-actions">
                      <button
                        className="build-interview__delete-confirm-btn build-interview__delete-confirm-btn--cancel"
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        No
                      </button>
                      <button
                        className="build-interview__delete-confirm-btn build-interview__delete-confirm-btn--confirm"
                        onClick={() => handleConfirmDelete(pipelineToDelete)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}

                {/* Save Errors */}
                {saveErrors.length > 0 && (
                  <div className="build-interview__save-errors">
                    <div className="build-interview__save-errors-header">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span>Cannot save pipeline</span>
                      <button
                        className="build-interview__save-errors-close"
                        onClick={() => setSaveErrors([])}
                      >
                        ×
                      </button>
                    </div>
                    <ul className="build-interview__save-errors-list">
                      {saveErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Save Success */}
                {saveSuccess && (
                  <div className="build-interview__save-success">
                    <div className="build-interview__save-success-header">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span>Submitted! Verifying and scheduling the interview shortly</span>
                      <button
                        className="build-interview__save-success-close"
                        onClick={() => setSaveSuccess(false)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Center: ReactFlow Canvas */}
          <div 
            ref={canvasRef}
            className="build-interview__canvas build-interview__reactflow-wrapper"
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const type = e.dataTransfer.getData('application/interview-type') as InterviewType;
              if (!type) return;
              
              const emptySlot = rounds.find(slot => !slot.interview);
              if (emptySlot) {
                onDrop(e, emptySlot.id);
              }
            }}
          >
            {showCanvasEmptyState ? (
              <BuilderEmptyState variant="canvas" onNewInterview={handleCreateNewPipeline} />
            ) : (
              <>
            {/* Canvas Top Bar - Name and Save */}
            <div className="build-interview__canvas-topbar">
              <div className="build-interview__canvas-name">
                {isEditingName ? (
                  <input
                    type="text"
                    className="build-interview__pipeline-name-input"
                    value={pipelineName}
                    onChange={(e) => {
                      setPipelineName(e.target.value);
                      if (pipelineNameError) setPipelineNameError('');
                    }}
                    onBlur={() => setIsEditingName(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setIsEditingName(false);
                    }}
                    autoFocus
                    maxLength={30}
                  />
                ) : (
                  <button
                    className="build-interview__pipeline-name"
                    onClick={() => setIsEditingName(true)}
                    title="Click to rename"
                  >
                    <span>{pipelineName}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                )}
                {pipelineNameError && (
                  <span className="build-interview__pipeline-name-error">{pipelineNameError}</span>
                )}
              </div>
              <button 
                className={`build-interview__save-btn${isScheduling ? ' build-interview__save-btn--disabled' : ''}`}
                onClick={handleSavePipeline}
                disabled={isScheduling}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                {isScheduling ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>

            {/* Stats - Bottom Right */}
            <div className="build-interview__canvas-actions">
              <div className="build-interview__stats">
                <span className="build-interview__stat">
                  <strong>{stats.filled}</strong>/{stats.total} rounds
                </span>
                <span className="build-interview__stat-divider">•</span>
                <span className="build-interview__stat">
                  <strong>{stats.configured}</strong> configured
                </span>
              </div>
            </div>

            {/* Instructions */}
            <div className="build-interview__instructions">
              <span>Paste job description <span className="build-interview__arrow"> › </span> Drag and drop interview type <span className="build-interview__arrow">›</span> configure <span className="build-interview__arrow">›</span> add more rounds <span className="build-interview__arrow">›</span> rename and save sequence</span>
            </div>

            {/* Hints - only show when there are hints */}
            {(rounds.length >= MAX_ROUNDS || (rounds.length > 3 && rounds.length < MAX_ROUNDS)) && (
              <div className="build-interview__hints build-interview__hints--overlay">
                {rounds.length >= MAX_ROUNDS && (
                  <span className="build-interview__hint">Maximum {MAX_ROUNDS} rounds allowed</span>
                )}
                {rounds.length > 3 && rounds.length < MAX_ROUNDS && (
                  <span className="build-interview__hint">Click × to remove extra rounds</span>
                )}
              </div>
            )}

            <ReactFlow
              key={`rf-${renderKey}-${rounds.length}`}
              nodes={reactFlowNodes}
              edges={reactFlowEdges}
              nodeTypes={nodeTypes}
              onInit={(instance) => {
                reactFlowInstanceRef.current = instance;
                fitCanvasView();
              }}
              onNodeClick={(_, node) => {
                if (node.type === 'jdNode') {
                  setIsJDSelected(true);
                  setSelectedSlotId(null);
                  setIsPanelOpen(true);
                } else if (node.type === 'roundNode') {
                  const slot = rounds.find(r => r.id === node.id);
                  if (slot?.interview) {
                    setSelectedSlotId(node.id);
                    setIsJDSelected(false);
                    setIsPanelOpen(true);
                  }
                } else if (node.type === 'addRoundNode') {
                  if (rounds.length < MAX_ROUNDS) {
                    addRound();
                  }
                }
              }}
              defaultViewport={{ x: 0, y: 0, zoom: 0.85 }}
              minZoom={0.5}
              maxZoom={2}
              defaultEdgeOptions={{
                type: 'bezier',
                animated: false,
              }}
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={true}
              panOnScroll={true}
              zoomOnScroll={true}
              panOnDrag={true}
              selectionOnDrag={false}
            >
              <Background 
                color="#333" 
                gap={20} 
                size={1}
                style={{ backgroundColor: '#141414' }}
              />
              <Controls 
                showZoom={true}
                showFitView={true}
                showInteractive={false}
                position="bottom-left"
                className="build-interview__rf-controls"
              />
            </ReactFlow>

            {/* Bottom: Interview Types Dock - Overlaid on canvas */}
            <div className="build-interview__dock build-interview__dock--overlay">
              <div className="build-interview__dock-items">
                {INTERVIEW_TYPES.map((type) => (
                  <div
                    key={type.id}
                    className={`build-interview__dock-item build-interview__dock-item--${type.id}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, type.id)}
                  >
                    <InterviewTypeIcon icon={type.icon} color={type.color} size={22} />
                    <span className="dock-label">{type.title}</span>
                  </div>
                ))}
              </div>
            </div>
              </>
            )}
          </div>

          {/* Right: Config Panel */}
          <ConfigPanel
            nodeData={selectedSlot?.interview || null}
            roundName={selectedSlot?.name || ''}
            scheduledDate={selectedSlot?.scheduledDate || ''}
            isDateLocked={isDateSyncEnabled}
            isDateSyncEnabled={isDateSyncEnabled}
            dateWarning={dateWarning}
            dateWarningClosing={dateWarningClosing}
            onDateSyncToggle={handleDateSyncToggle}
            isOpen={isPanelOpen && !showCanvasEmptyState}
            onToggle={() => setIsPanelOpen(!isPanelOpen)}
            onRenameRound={(name) => selectedSlotId && renameRound(selectedSlotId, name)}
            onScheduledDateChange={(date) => selectedSlotId && updateScheduledDate(selectedSlotId, date)}
            onUpdateConfig={onUpdateConfig}
            onClose={() => {
              setIsPanelOpen(false);
              // Delay clearing the selection to allow animation to complete
              setTimeout(() => {
                onCloseConfig();
                setIsJDSelected(false);
              }, 400);
            }}
            onMarkConfigured={onMarkConfigured}
            // JD Mode props
            isJDMode={isJDSelected}
            jdText={pastedText}
            jdWordCount={wordCount}
            isJDValid={isWordCountValid}
            isJDInvalid={isWordCountInvalid}
            onJDTextChange={setPastedText}
            isJDLocked={isExtractionLocked}
            onProcessJD={handleProcessJD}
            isJDProcessing={isExtractingSkills}
            isJDProcessed={isExtractionComplete}
            jdProcessingFailed={
              jdProcessing.status === 'failed' || jdProcessing.status === 'violation'
            }
          />
        </div>
          )}
        </div>
      )}

      {roleModalStack.length > 0 && (
        <JobRoleModalStack
          stack={roleModalStack}
          isBuilding={isBuildingFromRole}
          onCloseTop={handleCloseRoleModal}
          onBuildInterview={handleBuildFromRole}
          onRelatedRoleSelect={handleRelatedRoleSelect}
        />
      )}

      {/* Resume Editor Modal (opened after scheduled customise) */}
      <ResumeEditorModal
        isOpen={isResumeEditorOpen}
        onClose={handleCloseResumeEditor}
        initialContent={resumeEditorContent}
        onSave={(content) => setResumeEditorContent(content)}
      />

      <InterviewDetailModal
        interview={extractedInfoInterview}
        onClose={handleCloseExtractedInfoModal}
        isCheckingResume={isCheckingExtractedInfoResume}
        resumeExists={extractedInfoResume?.exists ?? false}
        resumeMarkdown={extractedInfoResume?.resumeMarkdown}
        modalSource="build-interview"
        canCustomise={activePipeline?.status === 'published'}
        customiseDisabledHint="Schedule the interview to customise your resume."
        isCustomising={isCustomisingResume}
        onCustomise={handleCustomiseFromExtractedInfoModal}
        onViewResume={(markdown) => {
          const lexicalContent = parseMarkdownToLexical(markdown);
          setResumeEditorContent(JSON.stringify(lexicalContent));
          handleCloseExtractedInfoModal();
          setIsResumeEditorOpen(true);
        }}
      />

      {savedNameTooltip && (
        <div
          className="build-interview__saved-name-tooltip"
          style={{
            top: savedNameTooltip.top,
            left: savedNameTooltip.left,
          }}
        >
          {savedNameTooltip.name}
        </div>
      )}
    </div>
  );
};

export default BuildInterview;
